import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Country {
  code: string;
  name: string;
  ddi: string;
  flag: string;
}

// Lista de países mais comuns com DDI e bandeiras (emojis)
const countries: Country[] = [
  { code: 'BR', name: 'Brasil', ddi: '55', flag: '🇧🇷' },
  { code: 'US', name: 'Estados Unidos', ddi: '1', flag: '🇺🇸' },
  { code: 'AR', name: 'Argentina', ddi: '54', flag: '🇦🇷' },
  { code: 'CL', name: 'Chile', ddi: '56', flag: '🇨🇱' },
  { code: 'CO', name: 'Colômbia', ddi: '57', flag: '🇨🇴' },
  { code: 'PE', name: 'Peru', ddi: '51', flag: '🇵🇪' },
  { code: 'UY', name: 'Uruguai', ddi: '598', flag: '🇺🇾' },
  { code: 'PY', name: 'Paraguai', ddi: '595', flag: '🇵🇾' },
  { code: 'BO', name: 'Bolívia', ddi: '591', flag: '🇧🇴' },
  { code: 'VE', name: 'Venezuela', ddi: '58', flag: '🇻🇪' },
  { code: 'EC', name: 'Equador', ddi: '593', flag: '🇪🇨' },
  { code: 'PT', name: 'Portugal', ddi: '351', flag: '🇵🇹' },
  { code: 'ES', name: 'Espanha', ddi: '34', flag: '🇪🇸' },
  { code: 'FR', name: 'França', ddi: '33', flag: '🇫🇷' },
  { code: 'IT', name: 'Itália', ddi: '39', flag: '🇮🇹' },
  { code: 'DE', name: 'Alemanha', ddi: '49', flag: '🇩🇪' },
  { code: 'GB', name: 'Reino Unido', ddi: '44', flag: '🇬🇧' },
  { code: 'MX', name: 'México', ddi: '52', flag: '🇲🇽' },
  { code: 'CA', name: 'Canadá', ddi: '1', flag: '🇨🇦' },
  { code: 'AU', name: 'Austrália', ddi: '61', flag: '🇦🇺' },
  { code: 'NZ', name: 'Nova Zelândia', ddi: '64', flag: '🇳🇿' },
  { code: 'JP', name: 'Japão', ddi: '81', flag: '🇯🇵' },
  { code: 'CN', name: 'China', ddi: '86', flag: '🇨🇳' },
  { code: 'IN', name: 'Índia', ddi: '91', flag: '🇮🇳' },
  { code: 'RU', name: 'Rússia', ddi: '7', flag: '🇷🇺' },
  { code: 'ZA', name: 'África do Sul', ddi: '27', flag: '🇿🇦' },
];

interface CountrySelectProps {
  value?: string;
  onValueChange: (ddi: string, country: Country) => void;
  disabled?: boolean;
}

export const CountrySelect = ({ value, onValueChange, disabled }: CountrySelectProps) => {
  const [open, setOpen] = useState(false);
  
  // Encontrar o país selecionado pelo DDI
  const selectedCountry = countries.find(country => country.ddi === value) || countries[0]; // Brasil como padrão
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[140px] justify-between h-10"
          disabled={disabled}
        >
          <span className="flex items-center gap-2">
            <span className="text-lg">{selectedCountry.flag}</span>
            <span className="text-sm font-medium">+{selectedCountry.ddi}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar país..." />
          <CommandList>
            <CommandEmpty>Nenhum país encontrado.</CommandEmpty>
            <CommandGroup>
              {countries.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name} ${country.ddi} ${country.code}`}
                  onSelect={() => {
                    onValueChange(country.ddi, country);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === country.ddi ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="text-lg mr-2">{country.flag}</span>
                  <span className="flex-1">{country.name}</span>
                  <span className="text-muted-foreground ml-2">+{country.ddi}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
